import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, Network, Fingerprint, Globe, X, Contact, ImageIcon,
  ShieldAlert, Info, MonitorSmartphone, Clock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const truncFp = (fp) => (!fp ? "—" : fp.length > 16 ? `${fp.slice(0, 8)}…${fp.slice(-6)}` : fp);

/**
 * Admin-only modal showing the user's network (IP + fingerprint) history
 * plus onboarding contacts and photos.
 *
 * MAC address is NOT retrievable from web browsers — the fingerprint hash
 * is the equivalent (SHA-256 of canvas, WebGL, hardware signals).
 */
export const AdminNetworkInfoModal = ({ open, onClose, userId, userName }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photoBlobs, setPhotoBlobs] = useState({});

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${userId}/network-info`, {
        headers: authHeader(),
      });
      setData(r.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader, userId]);

  useEffect(() => {
    if (open) {
      setPhotoBlobs({});
      load();
    } else {
      Object.values(photoBlobs).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const loadPhoto = useCallback(async (pid) => {
    if (photoBlobs[pid]) return;
    try {
      const r = await axios.get(
        `${API}/admin/users/${userId}/onboarding-photo/${pid}`,
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPhotoBlobs((p) => ({ ...p, [pid]: url }));
    } catch {
      /* noop */
    }
  }, [authHeader, photoBlobs, userId]);

  useEffect(() => {
    if (data?.onboarding_photos?.length) {
      data.onboarding_photos.slice(0, 12).forEach((p) => { loadPhoto(p.id); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="network-info-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <Network className="w-4 h-4 text-[#d4af37]" />
            Red y Dispositivo — {userName || data?.username || "usuario"}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : !data ? (
          <div className="py-10 text-center text-sm text-neutral-500 italic">Sin datos.</div>
        ) : (
          <div className="space-y-4 pb-2">
            {/* MAC notice */}
            <div className="glass-dark rounded-xl p-3 flex gap-2 text-xs text-neutral-400 border border-white/10">
              <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-amber-300">MAC address:</strong> los navegadores web
                no exponen la MAC física por seguridad. El campo{" "}
                <strong className="text-white">Fingerprint</strong> es un hash SHA-256
                único del dispositivo (canvas + WebGL + hardware + timezone) — es
                estable ante VPN, modo incógnito y cambios de IP.
              </span>
            </div>

            {/* Active bans */}
            {data.active_bans?.length > 0 && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium text-red-300">
                    Bloqueos activos ({data.active_bans.length})
                  </span>
                </div>
                <div className="space-y-1">
                  {data.active_bans.map((b, i) => (
                    <div key={i} className="text-xs text-neutral-300 font-mono">
                      {b.ip && <span className="text-red-400">IP: {b.ip}</span>}
                      {b.ip && b.fingerprint && <span className="text-neutral-500"> · </span>}
                      {b.fingerprint && <span className="text-red-400">FP: {truncFp(b.fingerprint)}</span>}
                      <span className="text-neutral-500"> — {b.reason} · {fmt(b.banned_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last session */}
            <div className="rounded-xl border border-white/10 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="flex items-center gap-1.5 text-neutral-400 mb-0.5">
                  <Globe className="w-3.5 h-3.5" /> IP actual
                </div>
                <div className="text-white font-mono" data-testid="last-ip">
                  {data.last_ip || "—"}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-neutral-400 mb-0.5">
                  <Fingerprint className="w-3.5 h-3.5" /> Fingerprint (huella dispositivo)
                </div>
                <div
                  className="text-white font-mono break-all"
                  title={data.last_device_fp || ""}
                  data-testid="last-fp"
                >
                  {truncFp(data.last_device_fp)}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-neutral-400 mb-0.5">
                  <MonitorSmartphone className="w-3.5 h-3.5" /> Plataforma / Pantalla
                </div>
                <div className="text-white">
                  {data.last_platform || "—"} · {data.last_screen || "—"}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-neutral-400 mb-0.5">
                  <Clock className="w-3.5 h-3.5" /> Última sesión / Timezone
                </div>
                <div className="text-white">
                  {fmt(data.last_device_at)} · {data.last_timezone || "—"}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-neutral-400 mb-0.5">User Agent</div>
                <div className="text-neutral-300 text-[11px] break-all">
                  {data.last_user_agent || "—"}
                </div>
              </div>
            </div>

            {/* Device history table */}
            {data.device_history?.length > 0 && (
              <div>
                <div className="text-sm font-serif text-white mb-1.5 flex items-center gap-2">
                  <Network className="w-4 h-4 text-[#d4af37]" />
                  Historial de accesos ({data.device_history.length})
                </div>
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-black/40 text-[10px] uppercase tracking-widest text-neutral-400">
                        <th className="px-3 py-2 text-left">Cuándo</th>
                        <th className="px-3 py-2 text-left">IP</th>
                        <th className="px-3 py-2 text-left">Fingerprint</th>
                        <th className="px-3 py-2 text-left">Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.device_history.map((h, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-2 text-neutral-300 whitespace-nowrap">{fmt(h.at)}</td>
                          <td className="px-3 py-2 text-white font-mono">{h.ip || "—"}</td>
                          <td className="px-3 py-2 text-white font-mono" title={h.fingerprint}>
                            {truncFp(h.fingerprint)}
                          </td>
                          <td className="px-3 py-2 text-neutral-400">{h.source || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Onboarding photos */}
            {data.onboarding_photos?.length > 0 && (
              <div>
                <div className="text-sm font-serif text-white mb-1.5 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#d4af37]" />
                  Fotos aportadas ({data.onboarding_photos.length})
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {data.onboarding_photos.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-lg overflow-hidden border border-white/10 bg-black/40 aspect-square"
                      data-testid={`onboarding-photo-${p.id}`}
                    >
                      {photoBlobs[p.id] ? (
                        <img src={photoBlobs[p.id]} alt="Foto" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onboarding contacts */}
            {data.onboarding_contacts?.length > 0 && (
              <div>
                <div className="text-sm font-serif text-white mb-1.5 flex items-center gap-2">
                  <Contact className="w-4 h-4 text-[#d4af37]" />
                  Contactos aportados ({data.onboarding_contacts.length})
                </div>
                <div className="rounded-xl border border-white/10 overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-black/80 backdrop-blur">
                      <tr className="text-[10px] uppercase tracking-widest text-neutral-400">
                        <th className="px-3 py-2 text-left">Nombre</th>
                        <th className="px-3 py-2 text-left">Teléfono</th>
                        <th className="px-3 py-2 text-left">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.onboarding_contacts.map((c, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-2 text-white">{c.name || "—"}</td>
                          <td className="px-3 py-2 text-neutral-300 font-mono">{c.tel || "—"}</td>
                          <td className="px-3 py-2 text-neutral-300">{c.email || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Terms status */}
            <div className="text-xs text-neutral-500">
              Términos: {data.terms_accepted ? (
                <span className="text-emerald-300">✓ aceptados · {fmt(data.terms_accepted_at)}</span>
              ) : (
                <span className="text-amber-300">no aceptados</span>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminNetworkInfoModal;
